import React, { useEffect, useRef, useState, useCallback } from "react";
import { Box, IconButton, Typography, CircularProgress } from "@mui/material";
import { Refresh, CheckCircle, Cancel, DragIndicator } from "@mui/icons-material";

const SliderCaptcha = ({ data, loading, onRefresh, onPositionChange, status }) => {
  const trackRef = useRef(null);
  const [dragX, setDragX] = useState(0);
  const [dragY, setDragY] = useState(0);
  const [dragging, setDragging] = useState(false);

  const challenge = data?.challenge;
  const width = challenge?.width || 400;
  const height = challenge?.height || 300;
  const pieceSize = challenge?.pieceSize || 48;
  const maxX = Math.max(0, width - pieceSize);
  const maxY = Math.max(0, height - pieceSize);

  useEffect(() => {
    // Start piece at top-left
    setDragX(8);
    setDragY(8);
  }, [data?.token]);

  const submitPosition = useCallback(
    (x, y) => {
      if (onPositionChange) onPositionChange({ x: Math.round(x), y: Math.round(y) });
    },
    [onPositionChange]
  );

  const handlePointerDown = (e) => {
    if (!challenge || status === "ok") return;
    setDragging(true);
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {}
  };

  const handlePointerMove = (e) => {
    if (!dragging || !trackRef.current) return;
    const rect = trackRef.current.getBoundingClientRect();
    const scaleX = rect.width / width;
    const scaleY = rect.height / height;
    const localX = (e.clientX - rect.left) / scaleX - pieceSize / 2;
    const localY = (e.clientY - rect.top) / scaleY - pieceSize / 2;
    setDragX(Math.max(0, Math.min(maxX, localX)));
    setDragY(Math.max(0, Math.min(maxY, localY)));
  };

  const handlePointerUp = (e) => {
    if (!dragging) return;
    setDragging(false);
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {}
    submitPosition(dragX, dragY);
  };

  const slots = challenge?.slots || [];
  const bg1 = challenge?.bgColor1 || "#90caf9";
  const bg2 = challenge?.bgColor2 || "#7e57c2";
  const tokenSuffix = data?.token ? data.token.slice(-8) : "x";
  const clipId = `piece-clip-${tokenSuffix}`;
  const bgGradId = `captcha-bg-${tokenSuffix}`;

  const borderColor =
    status === "ok" ? "success.main" : status === "fail" ? "error.main" : "divider";

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Drag the puzzle piece to the slot where the picture fits seamlessly
      </Typography>

      <Box
        sx={{
          position: "relative",
          border: 2,
          borderColor,
          borderRadius: 2,
          overflow: "hidden",
          width: "100%",
          maxWidth: width,
          aspectRatio: `${width} / ${height}`,
          backgroundColor: "background.default",
        }}
        ref={trackRef}
      >
        {loading || !challenge ? (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress size={24} />
          </Box>
        ) : (
          <svg
            viewBox={`0 0 ${width} ${height}`}
            width="100%"
            height="100%"
            style={{ display: "block", touchAction: "none" }}
          >
            <defs>
              <linearGradient id={bgGradId} x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor={bg1} />
                <stop offset="100%" stopColor={bg2} />
              </linearGradient>
              <clipPath id={clipId}>
                <rect width={pieceSize} height={pieceSize} rx={6} ry={6} />
              </clipPath>
            </defs>

            {/* Background */}
            <rect width={width} height={height} fill={`url(#${bgGradId})`} />
            {challenge.bgImageUrl && (
              <image
                href={challenge.bgImageUrl}
                xlinkHref={challenge.bgImageUrl}
                x={0}
                y={0}
                width={width}
                height={height}
                preserveAspectRatio="xMidYMid slice"
              />
            )}

            {/* Slot cutouts — identical squares (target + decoy) */}
            {slots.map((slot, idx) => (
              <g key={idx} transform={`translate(${slot.x}, ${slot.y})`}>
                <rect
                  width={pieceSize}
                  height={pieceSize}
                  rx={6}
                  ry={6}
                  fill="rgba(0,0,0,0.55)"
                  stroke="rgba(255,255,255,0.85)"
                  strokeWidth={1.5}
                />
              </g>
            ))}

            {/* Draggable puzzle piece — shows the bg image fragment from the real slot */}
            <g
              transform={`translate(${dragX}, ${dragY})`}
              style={{ cursor: status === "ok" ? "default" : dragging ? "grabbing" : "grab" }}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerCancel={handlePointerUp}
            >
              <g clipPath={`url(#${clipId})`}>
                {challenge.bgImageUrl && (
                  <image
                    href={challenge.bgImageUrl}
                    xlinkHref={challenge.bgImageUrl}
                    x={-challenge.pieceSourceX}
                    y={-challenge.pieceSourceY}
                    width={width}
                    height={height}
                    preserveAspectRatio="xMidYMid slice"
                  />
                )}
              </g>
              <rect
                width={pieceSize}
                height={pieceSize}
                rx={6}
                ry={6}
                fill="none"
                stroke="#ffffff"
                strokeWidth={2.5}
                style={{ filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.5))" }}
              />
            </g>
          </svg>
        )}

        {status === "ok" && (
          <Box
            sx={{
              position: "absolute",
              top: 6,
              right: 6,
              bgcolor: "success.main",
              color: "white",
              borderRadius: "50%",
              p: 0.3,
              display: "flex",
            }}
          >
            <CheckCircle fontSize="small" />
          </Box>
        )}
        {status === "fail" && (
          <Box
            sx={{
              position: "absolute",
              top: 6,
              right: 6,
              bgcolor: "error.main",
              color: "white",
              borderRadius: "50%",
              p: 0.3,
              display: "flex",
            }}
          >
            <Cancel fontSize="small" />
          </Box>
        )}

        <IconButton
          size="small"
          onClick={onRefresh}
          disabled={loading}
          aria-label="refresh captcha"
          sx={{
            position: "absolute",
            bottom: 6,
            right: 6,
            bgcolor: "rgba(0,0,0,0.55)",
            color: "white",
            "&:hover": { bgcolor: "rgba(0,0,0,0.75)" },
            "&.Mui-disabled": { color: "rgba(255,255,255,0.5)" },
          }}
        >
          <Refresh fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
        <DragIndicator fontSize="small" sx={{ color: "text.secondary" }} />
        <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1 }}>
          {status === "ok"
            ? "Verified ✓"
            : status === "fail"
            ? "Wrong slot — try again"
            : "Drag the piece to the slot where the picture fits"}
        </Typography>
      </Box>
    </Box>
  );
};

export default SliderCaptcha;
